import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Dialog } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import VideoPlayer from './VideoPlayer';
import VideoPoster from './VideoPoster';
import type { MediaItem, MediaLayout } from '../types/common';

interface MediaGalleryProps {
  media: MediaItem[];
  layout?: MediaLayout;
  maxHeight?: string | number;
}

const MediaContent: FC<{
  item: MediaItem;
  maxHeight?: string | number;
  rounded?: boolean;
  cover?: boolean;
}> = ({ item, maxHeight, rounded = true, cover = false }) => {
  const sx = {
    width: '100%',
    height: cover ? '100%' : 'auto',
    maxHeight,
    borderRadius: rounded ? 'var(--sh-lg)' : 0,
    objectFit: 'cover' as const,
    display: 'block',
  };

  if (item.type === 'Video') {
    // Grid tiles are small, non-playing stills that open the lightbox — a
    // poster keeps them from downloading video metadata for a thumbnail.
    if (cover) {
      // Grid tiles are at most half the content width (~350px CSS).
      return <VideoPoster url={item.url} duration={item.duration} width={480} sx={sx} />;
    }
    return (
      <VideoPlayer
        src={item.url}
        duration={item.duration}
        maxHeight={maxHeight}
        rounded={rounded}
      />
    );
  }
  return <Box component="img" src={item.url} alt="" sx={sx} />;
};

type Transform = { scale: number; x: number; y: number };
const MIN_SCALE = 1;
const MAX_SCALE = 5;

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const distance = (a: { x: number; y: number }, b: { x: number; y: number }) =>
  Math.hypot(a.x - b.x, a.y - b.y);
const midpoint = (a: { x: number; y: number }, b: { x: number; y: number }) => ({
  x: (a.x + b.x) / 2,
  y: (a.y + b.y) / 2,
});

/**
 * A pannable, zoomable image for the full-screen viewer. The whole image is
 * shown (object-fit: contain, never clipped); zoom is driven by wheel on web
 * and pinch on touch, with drag-to-pan and double-tap to toggle zoom.
 */
const ZoomableImage: FC<{ src: string }> = ({ src }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [t, setT] = useState<Transform>({ scale: 1, x: 0, y: 0 });
  // Client-coordinate positions of active pointers, keyed by pointerId.
  const pointers = useRef<Map<number, { x: number; y: number }>>(new Map());
  const lastPinch = useRef<{ dist: number; mid: { x: number; y: number } } | null>(null);

  // Keep the image center inside the container as it grows, so it can't be
  // dragged completely out of view.
  const bounded = (rect: DOMRect, scale: number, x: number, y: number): Transform => {
    if (scale <= 1) return { scale: 1, x: 0, y: 0 };
    const bx = (rect.width / 2) * scale;
    const by = (rect.height / 2) * scale;
    return { scale, x: clamp(x, -bx, bx), y: clamp(y, -by, by) };
  };

  // Zoom by `factor` while keeping the point under (clientX, clientY) fixed.
  const zoomAtClient = useCallback((clientX: number, clientY: number, factor: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    setT(prev => {
      const scale = clamp(prev.scale * factor, MIN_SCALE, MAX_SCALE);
      const ratio = scale / prev.scale;
      const gx = clientX - cx;
      const gy = clientY - cy;
      const x = gx * (1 - ratio) + prev.x * ratio;
      const y = gy * (1 - ratio) + prev.y * ratio;
      return bounded(rect, scale, x, y);
    });
  }, []);

  // Native wheel listener so we can preventDefault (React's onWheel is passive).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      zoomAtClient(e.clientX, e.clientY, e.deltaY < 0 ? 1.15 : 1 / 1.15);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomAtClient]);

  const onPointerDown = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      lastPinch.current = { dist: distance(a, b), mid: midpoint(a, b) };
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const prev = pointers.current.get(e.pointerId);
    if (!prev) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];

    if (pts.length >= 2 && lastPinch.current) {
      // Pinch: scale by finger-distance ratio around the moving midpoint.
      const nd = distance(pts[0], pts[1]);
      const nm = midpoint(pts[0], pts[1]);
      const factor = nd / lastPinch.current.dist;
      const panDx = nm.x - lastPinch.current.mid.x;
      const panDy = nm.y - lastPinch.current.mid.y;
      const el = containerRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        setT(p => {
          const scale = clamp(p.scale * factor, MIN_SCALE, MAX_SCALE);
          const ratio = scale / p.scale;
          const x = (nm.x - cx) * (1 - ratio) + p.x * ratio + panDx;
          const y = (nm.y - cy) * (1 - ratio) + p.y * ratio + panDy;
          return bounded(rect, scale, x, y);
        });
      }
      lastPinch.current = { dist: nd, mid: nm };
    } else if (pts.length === 1) {
      // Single-pointer drag pans, but only once zoomed in.
      const dx = e.clientX - prev.x;
      const dy = e.clientY - prev.y;
      setT(p => {
        if (p.scale <= 1) return p;
        const el = containerRef.current;
        if (!el) return p;
        return bounded(el.getBoundingClientRect(), p.scale, p.x + dx, p.y + dy);
      });
    }
  };

  const endPointer = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) lastPinch.current = null;
  };

  const onDoubleClick = (e: React.MouseEvent) => {
    zoomAtClient(e.clientX, e.clientY, t.scale > 1 ? 1 / t.scale : 2.5);
  };

  return (
    <Box
      ref={containerRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
      onDoubleClick={onDoubleClick}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        touchAction: 'none',
        cursor: t.scale > 1 ? 'grab' : 'zoom-in',
      }}
    >
      <Box
        component="img"
        src={src}
        alt=""
        draggable={false}
        sx={{
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
          userSelect: 'none',
          transform: `translate(${t.x}px, ${t.y}px) scale(${t.scale})`,
          transformOrigin: 'center center',
          willChange: 'transform',
        }}
      />
    </Box>
  );
};

/** Full-screen viewer: whole media shown uncropped, zoomable, with nav. */
const Lightbox: FC<{ media: MediaItem[]; initialIndex: number; onClose: () => void }> = ({
  media,
  initialIndex,
  onClose,
}) => {
  const theme = useTheme();
  const [index, setIndex] = useState(initialIndex);
  const go = (delta: number) => setIndex(i => (i + delta + media.length) % media.length);
  const item = media[index];

  const navBtnSx = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 2,
    backgroundColor: 'rgba(0,0,0,0.5)',
    color: '#fff',
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
  } as const;

  return (
    <Dialog
      open
      fullScreen
      onClose={onClose}
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(0,0,0,0.94)',
          backdropFilter: 'none',
          borderRadius: 0,
          boxShadow: 'none',
        },
      }}
    >
      <Box sx={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
        {item.type === 'Video' ? (
          <Box
            sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <VideoPlayer
              key={item.url}
              src={item.url}
              duration={item.duration}
              contain
              rounded={false}
              autoPlay
            />
          </Box>
        ) : (
          // Remount per image so zoom/pan resets on navigation.
          <ZoomableImage key={item.url} src={item.url} />
        )}

        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2, color: '#fff', backgroundColor: 'rgba(0,0,0,0.5)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' } }}
        >
          <CloseIcon />
        </IconButton>

        {media.length > 1 && (
          <>
            <Box
              sx={{
                position: 'absolute',
                top: 'env(safe-area-inset-top, 12px)',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 2,
                backgroundColor: 'rgba(0,0,0,0.6)',
                color: '#fff',
                fontFamily: theme.tokens.typography.monoFontFamily,
                fontSize: '11px',
                px: 1.25,
                py: '4px',
                borderRadius: '999px',
              }}
            >
              {index + 1} / {media.length}
            </Box>
            <IconButton onClick={() => go(-1)} aria-label="Previous" sx={{ ...navBtnSx, left: 12 }}>
              <ChevronLeftIcon />
            </IconButton>
            <IconButton onClick={() => go(1)} aria-label="Next" sx={{ ...navBtnSx, right: 12 }}>
              <ChevronRightIcon />
            </IconButton>
          </>
        )}
      </Box>
    </Dialog>
  );
};

const Carousel: FC<{
  media: MediaItem[];
  maxHeight?: string | number;
  onOpen?: (index: number) => void;
}> = ({ media, maxHeight, onOpen }) => {
  const theme = useTheme();
  const [index, setIndex] = useState(0);
  const go = (delta: number) => setIndex((i) => (i + delta + media.length) % media.length);
  const current = media[index];
  const canOpen = Boolean(onOpen) && current.type === 'Image';

  const navBtnSx = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: 'rgba(0,0,0,0.5)',
    color: '#fff',
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        onClick={canOpen ? () => onOpen!(index) : undefined}
        sx={{ cursor: canOpen ? 'zoom-in' : 'default' }}
      >
        <MediaContent item={current} maxHeight={maxHeight} />
      </Box>

      {/* counter badge */}
      <Box
        sx={{
          position: 'absolute',
          top: 'env(safe-area-inset-top, 12px)',
          right: 12,
          backgroundColor: 'rgba(0,0,0,0.6)',
          color: '#fff',
          fontFamily: theme.tokens.typography.monoFontFamily,
          fontSize: '10px',
          px: 1,
          py: '3px',
          borderRadius: '999px',
        }}
      >
        {index + 1} / {media.length}
      </Box>

      <IconButton size="small" onClick={() => go(-1)} sx={{ ...navBtnSx, left: 8 }}>
        <ChevronLeftIcon />
      </IconButton>
      <IconButton size="small" onClick={() => go(1)} sx={{ ...navBtnSx, right: 8 }}>
        <ChevronRightIcon />
      </IconButton>

      {/* dots */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 12,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: '6px',
        }}
      >
        {media.map((_, i) => (
          <Box
            key={i}
            onClick={() => setIndex(i)}
            sx={{
              height: '6px',
              width: i === index ? '18px' : '6px',
              borderRadius: '3px',
              cursor: 'pointer',
              backgroundColor: i === index ? '#fff' : 'rgba(255,255,255,0.45)',
              transition: 'width 0.2s ease',
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

const MediaGallery: FC<MediaGalleryProps> = ({ media, layout = 'carousel', maxHeight }) => {
  const theme = useTheme();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const previewHeight = maxHeight ?? theme.tokens.layout.mediaPreviewHeight;

  if (!media || media.length === 0) return null;

  const lightbox = lightboxIndex !== null && (
    <Lightbox media={media} initialIndex={lightboxIndex} onClose={() => setLightboxIndex(null)} />
  );

  if (media.length === 1) {
    const only = media[0];
    const canOpen = only.type === 'Image';
    return (
      <Box sx={{ mb: 2 }}>
        <Box
          onClick={canOpen ? () => setLightboxIndex(0) : undefined}
          sx={{ cursor: canOpen ? 'zoom-in' : 'default' }}
        >
          <MediaContent item={only} maxHeight={previewHeight} />
        </Box>
        {lightbox}
      </Box>
    );
  }

  if (layout === 'grid') {
    // Column spans on a 6-track grid so every row is completely filled:
    // rows of 3 (span 2), with the remainder rendered as half-width tiles
    // (span 3) — 2 leftovers become one row of 2; 1 leftover borrows the
    // previous row to make 2+2. A full rectangle means the wrapper's
    // border-radius clips every outer corner cleanly (no sharp edges).
    const remainder = media.length % 3;
    const halfWidthCount = remainder === 0 ? 0 : remainder === 2 ? 2 : 4;
    const thirdWidthCount = media.length - halfWidthCount;

    return (
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, 1fr)',
            gap: '3px',
            borderRadius: 'var(--radius-card)',
            overflow: 'hidden',
          }}
        >
          {media.map((item, i) => (
            <Box
              key={`${item.fileId}-${i}`}
              onClick={() => setLightboxIndex(i)}
              sx={{
                position: 'relative',
                aspectRatio: '1 / 1',
                cursor: 'pointer',
                overflow: 'hidden',
                backgroundColor: theme.palette.background.default,
                gridColumn: `span ${i < thirdWidthCount ? 2 : 3}`,
              }}
            >
              <MediaContent item={item} rounded={false} cover />
            </Box>
          ))}
        </Box>
        {lightbox}
      </Box>
    );
  }

  // default: carousel — the inline image opens the same full-screen viewer
  return (
    <Box sx={{ mb: 2 }}>
      <Carousel media={media} maxHeight={previewHeight} onOpen={(i) => setLightboxIndex(i)} />
      {lightbox}
    </Box>
  );
};

export default MediaGallery;
